import { Injectable } from '@angular/core';
import { ScorecardStore } from './scorecard.store';
import { Scorecard } from './models/scorecard.model';
import { mapTo, tap, take, map } from 'rxjs/operators';
import { ApiService } from '../../shared/services/core/api.service';
import { arrayUpdate } from '@datorama/akita';
import { ScorecardQuery } from './scorecard.query';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ScorecardCustomerPrizeOptionItemModel } from './models/scorecard-customer-prize-option.model';
import { finalize } from 'rxjs/operators';
import moment from 'moment/moment';
interface InitDataScorecardDescription {
  confettiColors: string[] | undefined;
}
/**
 * This scorecard service will handle
 * fetching the scorecards, set the
 * store and handle any interactions with
 * the scorecard store.
 *
 * The fetching of the data
 * will be called within the scorecard
 * resolver.
 */
@Injectable({ providedIn: 'root' })
export class ScorecardService {
  currentLcid: string | null;
  initDataScorecardDescription$: BehaviorSubject<InitDataScorecardDescription> =
    new BehaviorSubject<InitDataScorecardDescription>({
      confettiColors: undefined,
    });
  constructor(
    private scorecardStore: ScorecardStore,
    private api: ApiService,
    private scorecardQuery: ScorecardQuery
  ) {}

  /**
   * fetch the active scorecards from the api
   * and set the store.
   */
  fetchScorecards() {
    const request$: Observable<null> = this.api.get<Scorecard>('scorecard').pipe(
      tap((scorecards) => {
        scorecards = this.updateScorecardContent(scorecards);
        this.scorecardStore.set(scorecards);
      }),
      mapTo(null)
    );

    return this.scorecardQuery.getHasCache() ? of(null) : request$;
  }

  /**
   * Will update each scorecards
   * content by checking against the users
   * LCID and scorecard LCID.
   *
   * If no match is found then first
   * content found will be displayed on
   * the frontend.
   *
   * @param scorecards array of active scorecards from
   * the response.
   */
  updateScorecardContent(scorecards: Scorecard[]): Scorecard[] {
    this.currentLcid = localStorage.getItem('lcid');

    scorecards.forEach((scorecard) => {
      scorecard.Content.forEach((scorecardContent) => {
        if (scorecardContent.LCID.toString() == this.currentLcid) {
          scorecard.Content[0] = scorecardContent;
          scorecard.Actions.forEach((action) => {
            action.Content.forEach((actionContent) => {
              if (scorecardContent.LCID.toString() == actionContent.LCID.toString()) {
                action.Content[0] = actionContent;
              }
            });
          });
        }
      });
    });
    return scorecards;
  }

  /**
   * Set a scorecard action to be completed in the store
   */
  setScorecardActionToCompleted(scorecardId: number, scorecardActionId: number) {
    this.scorecardStore.update(scorecardId, ({ ActionsCompleted, Actions }) => ({
      ActionsCompleted: ActionsCompleted + 1,
      Actions: arrayUpdate(Actions, (a) => a.ScorecardActionId == scorecardActionId, {
        ScorecardActionId: scorecardActionId,
        CompletionDate: moment.utc().toDate(),
        ActionCompleted: true,
      }),
    }));
  }

  /**
   * Update scorecard milestone customer value within the
   * scorecard store with new customer value.
   * @param scorecardId ID of scorecard which will be updated in the scorecard store.
   * @param customerValue Customer value to replace current customer value of milestone progress within the store.
   */
  setScorecardMilesoneCustomerValueUpdate(scorecardId: number, customerValue: number) {
    this.scorecardStore.update(scorecardId, (scorecard) => ({
      ...scorecard,
      Milestone: {
        ...scorecard.Milestone,
        CustomerValue: customerValue,
      },
    }));
  }
  /**
   * Set a scorecard to be completed in the store, and unlock if has child-scorecard and it is locked
   */
  setScorecardToCompleted(currentScorecardId: number, completedDate: Date) {
    this.scorecardStore.update(currentScorecardId, { Completed: true, CompletionDate: completedDate });
    const hasLinkedScorecards = this.scorecardQuery.hasEntity(
      (s: Scorecard) => s.ParentId === currentScorecardId || s.ChildId === currentScorecardId
    );
    if (hasLinkedScorecards) {
      this.fetchScorecardBy(currentScorecardId)
        .pipe(
          take(1),
          map((linkedScorecards) => ({
            currentScorecard: {
              ...this.findScorecard(linkedScorecards, currentScorecardId),
              Completed: true,
              CompletionDate: completedDate,
            },
            childScorecard: linkedScorecards.find((s) => s.ParentId === currentScorecardId),
            linkedScorecards: linkedScorecards,
            isLinkedMoreThanTwo: linkedScorecards.length > 2,
          }))
        )
        .subscribe(({ currentScorecard, childScorecard, linkedScorecards, isLinkedMoreThanTwo }) => {
          if (isLinkedMoreThanTwo) {
            if (childScorecard) {
              currentScorecard = { ...currentScorecard, Hidden: true };
              childScorecard = { ...childScorecard, Hidden: false };
              const childOfChild = linkedScorecards.find((s) => s.ParentId == childScorecard?.ScorecardId);
              childOfChild && (childOfChild.Hidden = false);
              [currentScorecard, childScorecard, childOfChild].forEach((s) => {
                if (s) this.scorecardStore.update(s.ScorecardId, s);
              });
            } else {
              // if current scorecard doesn't have any child, that mean all scorecard are completed
              const newLinkedScorecards = linkedScorecards.map((s) => {
                if (s.ScorecardId == currentScorecardId) s = currentScorecard;
                return { ...s, Hidden: false };
              });
              this.scorecardStore.upsertMany(newLinkedScorecards);
            }
          } else {
            this.scorecardStore.update(childScorecard?.ScorecardId, { Locked: childScorecard?.Locked });
          }
        });
    }
  }

  /*
   * Triggers scorecard JoinWebCall event
   */
  joinWebCall(meetingNumber: string) {
    return this.api.post<number>('webcall/joined', parseInt(meetingNumber));
  }

  fetchScorecardBy(scorecardId: number): Observable<Scorecard[]> {
    return this.api.get<Scorecard>(`scorecard/details/${scorecardId}`).pipe(
      tap((scorecards) => {
        scorecards = this.updateScorecardContent(scorecards);
        let Milestone = this.scorecardQuery.getEntity(scorecardId)?.Milestone;
        this.scorecardStore.upsertMany(scorecards);
        scorecards.forEach((scorecard) => {
          let hasCustomerSelectedPrize = this.scorecardQuery.getEntity(scorecardId)?.HasCustomerSelectedPrize;
          this.scorecardStore.update(scorecard.ScorecardId, {
            ...scorecard,
            HasCustomerSelectedPrize: hasCustomerSelectedPrize,
            ...(Milestone ? { Milestone } : {}),
          });
        });
      })
    );
  }

  findScorecard(listScorecard: Scorecard[], id: number) {
    if (!id) return {} as Scorecard;
    return listScorecard.find((scorecard) => scorecard.ScorecardId === id) as Scorecard;
  }

  findScorecardIndex(listScorecard: Scorecard[], id: number) {
    if (!id) return -1;
    return listScorecard.findIndex((scorecard) => scorecard.ScorecardId === id);
  }

  selectPrize(scorecardId: number, prizeId: number) {
    return this.api.post(`scorecard/selectPrize/${scorecardId}/${prizeId}`, {}).pipe(
      finalize(() => {
        this.scorecardStore.update(scorecardId, (scorecard) => {
          let prizes = JSON.parse(JSON.stringify(scorecard?.PrizeOptions?.Prizes)) as ScorecardCustomerPrizeOptionItemModel[];
          prizes.forEach((prize) => {
            if (prize.PrizeId === prizeId) prize.Selected = true;
          });
          return {
            ...scorecard,
            HasCustomerSelectedPrize: true,
            PrizeOptions: {
              Prizes: prizes,
              CustomerHasSelected: true,
            },
          };
        });
      })
    );
  }
}
